import { zeroPad, generateFullName, dateToString } from '@/shared/lib';
import _ from 'lodash';
import { t } from 'ttag';
import { checkIcorDevice } from '@/entities/device';
import type { fetchUnreviewedReports } from '@/entities/inbox';
import type { FormattedInstantReport } from '../ui/inboxList/instantInboxList/instantInboxList.types';

type RawInstantReport = NonNullable<
  NonNullable<Awaited<ReturnType<typeof fetchUnreviewedReports>>>[number]
>;

/**
 * Formats the date-of-service range shown for an instant report in the inbox list. Converts the
 * start and stop instants with dateToString in the study timezone and offset, then joins them
 * with a dash.
 *
 * @param start - Report start instant.
 * @param stop - Report stop instant.
 * @param timezone - Study timezone used for formatting.
 * @param studyTimeOffset - Study time offset applied to both instants.
 * @param format - Date format of each end of the range.
 * @returns The start and stop dates joined as a range.
 */
const generateDateOfService = ({
  start,
  stop,
  timezone,
  studyTimeOffset,
  format,
}: {
  start: string;
  stop: string;
  timezone: string;
  studyTimeOffset: number;
  format: string;
}) => {
  const startDate = dateToString({
    isoDate: start,
    timezone,
    studyTimeOffset,
    format,
  });
  const stopDate = dateToString({
    isoDate: stop,
    timezone,
    studyTimeOffset,
    format,
  });
  return `${startDate} - ${stopDate}`;
};

/**
 * Flattens instant report records from the inbox query into the row shape the inbox list renders. Iterates the reports and lifts nested study, patient, physician, event, facility and inbox fields to the top level, deriving the physician and patient display names, the localised event time via dateToString and the date-of-service range via generateDateOfService, and setting isNotificationReport when the report type is 'Notification'.
 *
 * @param inboxes - Instant report records returned by the inbox query.
 * @returns Array of flattened instant report rows.
 */
const formatInstantReports = (inboxes: RawInstantReport[]) => {
  const formattedInboxes: FormattedInstantReport[] = [];
  _.forEach(inboxes, (report) => {
    formattedInboxes.push({
      id: report.id,
      reportId: report.reportId ?? 0,
      type: report.type ?? '',
      start: report.start ?? '',
      stop: report.stop ?? '',
      studyFid: report.study?.friendlyId ?? 0,
      physicianId: report.study?.info?.interprettingPhysician?.id ?? '',
      interprettingPhysicianName: generateFullName(report.study?.info?.interprettingPhysician),
      patientName: generateFullName(report.study?.info?.patient),
      patientId: report.study?.info?.patient?.patientId ?? '',
      study: report.study as FormattedInstantReport['study'],
      studyType: report.study?.studyType ?? '',
      timezone: report.study?.timezone ?? '',
      eventFid: report.event?.friendlyId ?? 0,
      physicianInterpretation: report.physicianInterpretation ?? '',
      technicianComments: report.technicianComments ?? '',
      date: report.inbox?.date ?? '',
      lastModified: report.lastModified ?? '',
      isReviewed: report.inbox?.isReviewed ?? false,
      isRead: report.inbox?.isRead ?? false,
      eventTimeUTC: report.start ?? '',
      eventTime: dateToString({
        startStudyDate: report.study?.start ?? undefined,
        isoDate: report.start ?? undefined,
        timezone: report.study?.timezone ?? undefined,
        studyTimeOffset: report.study?.studyTimeOffset ?? undefined,
        facilityCountry: report.study?.facility?.contact?.country ?? undefined,
        facilityTimezone: report.study?.facility?.timezone ?? undefined,
      }),
      dateOfService: generateDateOfService({
        start: report.start ?? '',
        stop: report.stop ?? '',
        timezone: report.study?.timezone ?? '',
        studyTimeOffset: report.study?.studyTimeOffset ?? 0,
        format: 'MM/DD/YY',
      }),
      eventId: report.event?.id ?? '',
      hasClinicData: report.event?.hasClinicData ?? false,
      isNotificationReport: report.type === 'Notification',
      isBiofluxDirect: report.study?.isBiofluxDirect ?? false,
      hasSignature: report.hasSignature ?? false,
      isArtifactReport: report.isArtifactReport ?? false,
      facility: report.study?.facility as FormattedInstantReport['facility'],
      facilityName: report.study?.facility?.name ?? '',
      formatVersion: report.formatVersion ?? '',
      aiFormatVersion: report.aiFormatVersion ?? '',
      reports: report.study?.reports as FormattedInstantReport['reports'],
    });
  });
  return formattedInboxes;
};

/**
 * Builds the badge labels shown on an instant report row in the inbox list. Always emits a zero-padded study label wired to open the study information drawer and a zero-padded report id label, then appends a 'Biodirect' badge for Bioflux Direct studies and an 'iCOR' badge when the study device passes the iCOR check.
 *
 * @param report - Formatted instant report row supplying the ids, flags and study.
 * @param toggleDisplayDrawer - Callback opening the drawer for a report with the given title.
 * @returns Array of label descriptors with text, modifier class and optional click handler.
 */
const generateLabelsInstantReport = (
  report: FormattedInstantReport,
  toggleDisplayDrawer: (report: FormattedInstantReport, title: string) => void,
) => {
  const labels: {
    text: string;
    className: string;
    isTextButton?: boolean;
    handleClick?: (e: React.MouseEvent<HTMLElement>) => void;
  }[] = [
    {
      text: `${t`Study`} ${zeroPad(report.studyFid)}`,
      className: '--study-fid',
      isTextButton: true,
      handleClick: (e) => {
        e.stopPropagation();
        toggleDisplayDrawer(report, t`Study information`);
      },
    },
    {
      text: `${t`Report`} #${zeroPad(report.reportId)}`,
      className: '--report-id',
    },
  ];
  if (report.isBiofluxDirect) {
    labels.push({
      text: t`Biodirect`,
      className: '--biodirect',
    });
  }
  if (checkIcorDevice(report.study?.device)) {
    labels.push({
      text: t`iCOR`,
      className: '--icor-device',
    });
  }
  return labels;
};

export { formatInstantReports, generateDateOfService, generateLabelsInstantReport };
